import React, {useState} from 'react';
import {ActivityIndicator, Platform, ScrollView, StyleSheet, View} from 'react-native';
import {Card} from '@rneui/themed';
import {observer} from 'mobx-react';

import NoTillPaymentServiceAvailableModal from '../../components/modals/NoPaymentConfiguredModal';
import CardTokeniserTillModal from '../../components/payments/till/cardTokeniser/CardTokeniserTillModal';
import PaymentMethodSelector from '../../components/payments/till/PaymentMethodSelector';
import type {PaymentMethodType} from '../../components/payments/till/PaymentMethodSelector/types';
import Button from '../../components/themed/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {PAYMENT_SERVICE_TYPES, usePaymentCard, usePaymentServices} from '../../queries/paymentCard';
import {CheckoutActionButton} from '../../screens/cart/components/CheckoutActionButton';
import ThreeDsMobileBrowserDataIframe from '../../screens/cart/components/ThreeDsMobileBrowserDataIframe';
import {
  createApplePayCheckoutConfigs,
  createGooglePayCheckoutConfigs,
  useCardManagement,
} from '../../screens/cart/hooks';
import useFetchMobileWebView3dsBrowserDataJSFocusEffect from '../../screens/cart/useFetchMobileWebView3dsBrowserDataJSFocusEffect';
import {getNativeBrowserData} from '../../screens/cart/util/threeDS';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {MAX_FORM_WIDTH} from '../constants';
import {GiftCardShopRouteNavigatorScreenProps} from '../types/navigationTypes';

import OrderTotals from './OrderTotals';

const LOG_TAG = 'GiftCardStoreCheckoutScreen';

export const GiftCardStoreCheckoutScreen = observer(
  ({navigation}: GiftCardShopRouteNavigatorScreenProps<AppRoute.GIFT_CARDS_STORE_CHECKOUT>) => {
    const {cartStore} = useStores();

    // Responsive action-button width: 80% on web desktop, 100% on mobile.
    // Shared by CONFIRM PAYMENT (incl. Apple Pay) and BACK so all three align.
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const buttonWidthStyle = {
      width: isWebDesktopScreen ? '80%' : '100%',
      alignSelf: 'center',
    } as const;

    // Get paymentCard for card checkout (need the id for checkoutOrder)
    const {data: paymentCardData} = usePaymentCard();
    const paymentCard = paymentCardData?.card;

    // Card management via consolidated hook (handles display and mutations)
    const {
      handleAddOrChangeCard,
      handleDeleteCard,
      isCardTokeniserModalVisible,
      setCardTokeniserModalVisible,
      isNoPaymentServiceModalVisible,
      setNoPaymentServiceModalVisible,
    } = useCardManagement();

    // Payment method selection and readiness state
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType>('card');
    const [canPay, setCanPay] = useState(true);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const {mobileWebView3DsBrowserDataJS, browserData, setBrowserData} =
      useFetchMobileWebView3dsBrowserDataJSFocusEffect();

    // Resolve Apple Pay and Google Pay service IDs from backend
    const {data: paymentServicesData} = usePaymentServices();
    const applePayServiceId =
      paymentServicesData?.services.find(s => s.type === PAYMENT_SERVICE_TYPES.APPLE_PAY)?.id ??
      null;
    const googlePayServiceId =
      paymentServicesData?.services.find(s => s.type === PAYMENT_SERVICE_TYPES.GOOGLE_PAY)?.id ??
      null;

    // Build wallet configs. On success, checkoutOrder routes to the gift card
    // confirmation via the pre-existing pollOrderStatus path (OrderProcessingScreen
    // branches on isSelectedStoreGiftCardStore), so no navigation is threaded here.
    const {applePayConfig, applePayIOSConfig} = createApplePayCheckoutConfigs({
      cartStore,
      applePayServiceId,
      logTag: LOG_TAG,
    });

    const {googlePayConfig, googlePayWebConfig} = createGooglePayCheckoutConfigs({
      cartStore,
      googlePayServiceId,
      logTag: LOG_TAG,
    });

    // Card payment path — called by CheckoutActionButton when method is 'card'
    const handleFormSubmit = async () => {
      setIsSubmitting(true);
      try {
        // checkoutOrder ignores its nav arg (routes via RootNavigation →
        // OrderProcessingScreen.pollOrderStatus, which branches to the gift card
        // confirmation) and surfaces its own errors via Toast — it never throws.
        // browserData is BrowserData|undefined only because the 3DS hook's
        // Platform.select lacks a default; coerce to a defined value at the call.
        await cartStore.checkoutOrder(
          undefined,
          browserData ?? getNativeBrowserData(),
          paymentCard?.id,
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleBack = () => navigation.navigate(AppRoute.GIFT_CARDS_STORE_REVIEW);

    if (Platform.OS !== 'web' && !mobileWebView3DsBrowserDataJS) {
      return (
        <ContentView>
          <ActivityIndicator />
        </ContentView>
      );
    }

    return (
      <ContentView style={styles.formView} testID="gift-card-checkout-screen">
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <Card containerStyle={styles.cardContainer}>
            <Text style={styles.sectionLabel}>PAYMENT METHOD</Text>
            <PaymentMethodSelector
              selectedMethod={selectedPaymentMethod}
              onMethodChange={setSelectedPaymentMethod}
              onAddCard={handleAddOrChangeCard}
              onChangeCard={handleAddOrChangeCard}
              onDeleteCard={handleDeleteCard}
              onCanPayChange={setCanPay}
            />

            <Card.Divider style={styles.divider} />

            <Text style={styles.sectionLabel}>ORDER SUMMARY</Text>
            <OrderTotals />
          </Card>

          <View style={styles.buttonGroup}>
            <CheckoutActionButton
              selectedPaymentMethod={selectedPaymentMethod}
              applePayConfig={applePayConfig}
              applePayIOSConfig={applePayIOSConfig}
              googlePayConfig={googlePayConfig}
              googlePayWebConfig={googlePayWebConfig}
              onPayNow={handleFormSubmit}
              isLoading={isSubmitting || cartStore.submittingOrder}
              isDisabled={isSubmitting || !canPay}
              title="CONFIRM PAYMENT"
              containerStyle={buttonWidthStyle}
              buttonStyle={styles.actionButton}
              testID="gift-card-checkout-confirm-button"
            />
            <Button
              disabled={isSubmitting || cartStore.submittingOrder}
              onPress={handleBack}
              title="BACK"
              type="outline"
              containerStyle={buttonWidthStyle}
              buttonStyle={styles.actionButton}
              testID="gift-card-checkout-back-button"
            />
          </View>
        </ScrollView>

        <ThreeDsMobileBrowserDataIframe
          mobileWebView3DsBrowserDataJS={mobileWebView3DsBrowserDataJS}
          onBrowserData={setBrowserData}
        />
        <CardTokeniserTillModal
          isVisible={isCardTokeniserModalVisible}
          onCancel={() => setCardTokeniserModalVisible(false)}
          navigationTarget={AppRoute.GIFT_CARDS_STORE_CHECKOUT}
        />
        <NoTillPaymentServiceAvailableModal
          onOK={() => setNoPaymentServiceModalVisible(false)}
          isVisible={isNoPaymentServiceModalVisible}
        />
      </ContentView>
    );
  },
);

const styles = StyleSheet.create({
  formView: {
    maxWidth: MAX_FORM_WIDTH,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  cardContainer: {
    borderRadius: 12,
    padding: 16,
    margin: 0,
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
      },
    }),
  },
  sectionLabel: {
    color: theme.colour.text.heading.primary,
    marginBottom: 12,
    fontSize: 12,
    fontWeight: '600',
  },
  divider: {
    marginVertical: 16,
  },
  buttonGroup: {
    marginTop: 24,
    alignItems: 'center',
    gap: 12,
  },
  actionButton: {
    width: '100%',
    height: 52,
    borderRadius: 8,
  },
});

export default GiftCardStoreCheckoutScreen;
